/**
 * src/lib/nav.ts — navigation shim.
 *
 * The v.3 prototype implemented routing with hash URLs and a global
 * `navigate(path)` function. After vendoring to Vite + react-router-dom
 * we keep the same call-site shape (`navigate('/library')`) so the
 * mechanical conversion stays minimal. Internally it dispatches a
 * window event that the App layer subscribes to via `useGlobalNavigate`
 * and forwards to react-router-dom's `useNavigate`.
 *
 * This indirection avoids the React rule against calling hooks from
 * non-component scope (`navigate` is called from arrow-callbacks in
 * primitives and screens that themselves are not Router-aware).
 *
 * NOTE: callers may also import `useNavigate` directly from
 * react-router-dom — this shim is for compatibility with prototype
 * call sites only.
 */
const NAV_EVENT = 'circa:navigate';

export function navigate(path: string): void {
  window.dispatchEvent(new CustomEvent(NAV_EVENT, { detail: path }));
}

export function subscribeNavigate(handler: (path: string) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<string>).detail);
  window.addEventListener(NAV_EVENT, listener as EventListener);
  return () => window.removeEventListener(NAV_EVENT, listener as EventListener);
}
